import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import ContactInbox from "../components/ContactInbox";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  BarChart3,
  Filter,
  LogOut,
  RefreshCw,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  TrendingUp,
  Users,
  CreditCard,
  FileText,
  MapPin,
  DollarSign,
  Percent,
  LogIn,
  Calculator,
  CheckCircle,
} from "lucide-react";
import { adminApi } from "../lib/adminApi";

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

const STATE_OPTIONS = [
  { value: "all", label: "All states" },
  { value: "California", label: "California" },
  { value: "Texas", label: "Texas" },
  { value: "Florida", label: "Florida" },
  { value: "New York", label: "New York" },
];

const PAID_OPTIONS = [
  { value: "all", label: "All users" },
  { value: "paid", label: "Paid only" },
  { value: "free", label: "Free / not paid" },
];

function StatCard({ icon: Icon, label, value, sub, accent = false, testId }) {
  return (
    <Card className={`p-5 ${accent ? "bg-gradient-to-br from-[var(--csd-primary-light)]/60 to-white" : ""}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-text-secondary)] mb-1">
            {label}
          </div>
          <div className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">
            {value}
          </div>
          {sub && <div className="text-xs text-[var(--csd-text-secondary)] mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-[var(--csd-primary-light)] text-[var(--csd-primary)] grid place-items-center shrink-0">
          <Icon size={18} />
        </div>
      </div>
    </Card>
  );
}

function fmtUSD(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "$0.00";
  return `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(2)}%`;
}
function fmtN(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US");
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

// -------------------- Login screen --------------------
function AdminLogin() {
  return (
    <div className="min-h-screen bg-[var(--csd-bg)] grid place-items-center px-6">
      <SEO title="Admin — CarSaleDoc" description="Internal admin panel." path="/admin" noindex />
      <Card className="max-w-md w-full p-8 sm:p-10 text-center" data-testid="admin-login-card">
        <div className="w-14 h-14 rounded-2xl bg-[var(--csd-primary)] text-white grid place-items-center mx-auto mb-5 shadow-md">
          <ShieldCheck size={26} />
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">CarSaleDoc Admin</h1>
        <p className="mt-2 text-sm text-[var(--csd-text-secondary)] leading-relaxed">
          Private internal panel. Sign in with the administrator Google account to continue.
        </p>
        <Button
          onClick={() => {
            window.location.href = adminApi.loginUrl();
          }}
          className="csd-btn-primary mt-6 rounded-xl h-11 px-6 w-full text-base font-semibold"
          data-testid="admin-login-google"
        >
          <LogIn size={16} className="mr-2" /> Sign in with Google
        </Button>
        <div className="mt-6 text-xs text-[var(--csd-text-secondary)]">
          Access is restricted to whitelisted administrator emails. Other Google accounts will be rejected.
        </div>
      </Card>
    </div>
  );
}

// -------------------- Auth callback handler --------------------
function useAuthCallback(onAuthed) {
  const hasProcessed = useRef(false);
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    if (!sid) return;
    (async () => {
      try {
        const data = await adminApi.exchangeSession(sid);
        // Strip the fragment from the URL so the session_id doesn't leak.
        window.history.replaceState({}, "", "/admin");
        onAuthed?.(data.user);
        toast.success(`Welcome ${data.user?.name || data.user?.email || ""}`);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "Sign-in failed";
        if (status === 403) {
          toast.error("This Google account is not authorized.");
        } else {
          toast.error(detail);
        }
        window.history.replaceState({}, "", "/admin");
        onAuthed?.(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// -------------------- Filter bar --------------------
function FilterBar({ filters, setFilters, onRefresh, refreshing }) {
  const [draftStart, setDraftStart] = useState(filters.start || "");
  const [draftEnd, setDraftEnd] = useState(filters.end || "");
  useEffect(() => {
    setDraftStart(filters.start || "");
    setDraftEnd(filters.end || "");
  }, [filters.start, filters.end]);
  return (
    <Card className="p-4 mb-6" data-testid="admin-filterbar">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-[var(--csd-text-secondary)] mr-2">
          <Filter size={14} /> Filters
        </div>
        <div>
          <div className="text-xs text-[var(--csd-text-secondary)] mb-1">Range</div>
          <Select
            value={filters.range}
            onValueChange={(v) => setFilters((f) => ({ ...f, range: v }))}
          >
            <SelectTrigger className="w-[170px] rounded-lg" data-testid="filter-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {filters.range === "custom" && (
          <>
            <div>
              <div className="text-xs text-[var(--csd-text-secondary)] mb-1">From</div>
              <Input
                type="date"
                value={draftStart.slice(0, 10)}
                onChange={(e) => setDraftStart(e.target.value)}
                onBlur={() => setFilters((f) => ({ ...f, start: draftStart ? `${draftStart}T00:00:00Z` : null }))}
                className="rounded-lg w-[160px]"
                data-testid="filter-start"
              />
            </div>
            <div>
              <div className="text-xs text-[var(--csd-text-secondary)] mb-1">To</div>
              <Input
                type="date"
                value={draftEnd.slice(0, 10)}
                onChange={(e) => setDraftEnd(e.target.value)}
                onBlur={() => setFilters((f) => ({ ...f, end: draftEnd ? `${draftEnd}T23:59:59Z` : null }))}
                className="rounded-lg w-[160px]"
                data-testid="filter-end"
              />
            </div>
          </>
        )}
        <div>
          <div className="text-xs text-[var(--csd-text-secondary)] mb-1">State</div>
          <Select
            value={filters.state}
            onValueChange={(v) => setFilters((f) => ({ ...f, state: v }))}
          >
            <SelectTrigger className="w-[150px] rounded-lg" data-testid="filter-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-xs text-[var(--csd-text-secondary)] mb-1">Users</div>
          <Select
            value={filters.paid_only}
            onValueChange={(v) => setFilters((f) => ({ ...f, paid_only: v }))}
          >
            <SelectTrigger className="w-[160px] rounded-lg" data-testid="filter-paid">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAID_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          onClick={onRefresh}
          disabled={refreshing}
          className="rounded-lg ml-auto"
          data-testid="filter-refresh"
        >
          {refreshing ? (
            <Loader2 size={14} className="animate-spin mr-2" />
          ) : (
            <RefreshCw size={14} className="mr-2" />
          )}
          Refresh
        </Button>
      </div>
    </Card>
  );
}

// -------------------- Dashboard content --------------------
function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ range: "all", state: "all", paid_only: "all", start: null, end: null });
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [wizardFunnel, setWizardFunnel] = useState([]);
  const [byState, setByState] = useState([]);
  const [payments, setPayments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [calcLeads, setCalcLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [errs, setErrs] = useState({ count: 0, lines: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const queryParams = useMemo(() => {
    const p = { range: filters.range };
    if (filters.state && filters.state !== "all") p.state = filters.state;
    if (filters.paid_only && filters.paid_only !== "all") p.paid_only = filters.paid_only;
    if (filters.range === "custom") {
      if (filters.start) p.start = filters.start;
      if (filters.end) p.end = filters.end;
    }
    return p;
  }, [filters]);

  const loadAll = async () => {
    setRefreshing(true);
    // Use allSettled so a single failing endpoint (e.g. errors panel in a
    // container without log access) does not break the whole dashboard.
    const calls = [
      adminApi.summary(queryParams),
      adminApi.funnel(queryParams),
      adminApi.wizardFunnel(queryParams),
      adminApi.byState({ range: queryParams.range, start: queryParams.start, end: queryParams.end }),
      adminApi.payments({ ...queryParams, limit: 200 }),
      adminApi.leads({ ...queryParams, limit: 300 }),
      adminApi.valuationLeads({ ...queryParams, limit: 500 }),
      adminApi.contactMessages({ limit: 200 }),
      adminApi.documents({ range: queryParams.range, start: queryParams.start, end: queryParams.end }),
      adminApi.errors({ limit: 80 }),
    ];
    const results = await Promise.allSettled(calls);
    const [s, fn, wf, bs, pay, ld, cl, msgs, doc, er] = results;

    // If ANY endpoint returned 401 the session is dead — log out.
    const unauthorized = results.find((r) => r.status === "rejected" && r.reason?.response?.status === 401);
    if (unauthorized) {
      onLogout?.();
      navigate("/admin");
      setRefreshing(false);
      setLoading(false);
      return;
    }

    if (s.status === "fulfilled") setSummary(s.value);
    if (fn.status === "fulfilled") setFunnel(fn.value);
    if (wf.status === "fulfilled") setWizardFunnel(wf.value);
    if (bs.status === "fulfilled") setByState(bs.value);
    if (pay.status === "fulfilled") setPayments(pay.value);
    if (ld.status === "fulfilled") setLeads(ld.value);
    if (cl.status === "fulfilled") setCalcLeads(cl.value);
    if (msgs.status === "fulfilled") {
      setMessages(msgs.value.messages || []);
      setUnreadMessages(msgs.value.unread_count || 0);
    }
    if (doc.status === "fulfilled") setDocuments(doc.value);
    if (er.status === "fulfilled") setErrs(er.value);

    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length === results.length) {
      toast.error("Failed to load admin data");
    } else if (failed.length > 0) {
      // Some panels failed but most loaded — show a soft warning so the user
      // still sees the metrics that DID load.
      toast.warning(`${failed.length} of ${results.length} panels failed to load — partial data shown.`);
    }
    setRefreshing(false);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  return (
    <div className="min-h-screen bg-[var(--csd-bg)]">
      <SEO title="Admin — CarSaleDoc" description="Internal admin panel." path="/admin" noindex />

      {/* Header */}
      <header className="border-b border-[var(--csd-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-[var(--csd-primary)]" />
            <div className="font-heading font-bold text-lg">CarSaleDoc Admin</div>
            <Badge variant="outline" className="ml-2 text-xs">Internal</Badge>
          </div>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <div className="text-[var(--csd-text-secondary)] hidden sm:block">
              Signed in as <span className="font-semibold text-gray-900">{user.email}</span>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                await adminApi.logout().catch(() => {});
                onLogout?.();
              }}
              className="rounded-lg"
              data-testid="admin-logout"
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <FilterBar filters={filters} setFilters={setFilters} onRefresh={loadAll} refreshing={refreshing} />

        {loading ? (
          <div className="grid place-items-center py-20 text-[var(--csd-text-secondary)]">
            <Loader2 size={28} className="animate-spin text-[var(--csd-primary)]" />
            <div className="mt-3 text-sm">Loading dashboard…</div>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-summary">
              <StatCard icon={DollarSign} label="Revenue" value={fmtUSD(summary?.revenue_usd)} accent testId="stat-revenue" />
              <StatCard icon={CreditCard} label="Payments" value={fmtN(summary?.payments_completed)} testId="stat-payments" />
              <StatCard icon={TrendingUp} label="Avg ticket" value={fmtUSD(summary?.avg_ticket_usd)} testId="stat-avg-ticket" />
              <StatCard icon={Percent} label="Conversion" value={fmtPct(summary?.conversion_rate_pct)} sub="completed / started" testId="stat-conversion" />
              <StatCard icon={Users} label="Forms started" value={fmtN(summary?.forms_started)} testId="stat-forms-started" />
              <StatCard icon={FileText} label="Forms completed" value={fmtN(summary?.forms_completed)} testId="stat-forms-completed" />
              <StatCard icon={FileText} label="Documents generated" value={fmtN(summary?.documents_generated)} testId="stat-docs" />
              <StatCard icon={Percent} label="Checkout abandon." value={fmtPct(summary?.checkout_abandonment_pct)} sub="opened but not paid" testId="stat-checkout-abandon" />
            </section>

            {summary?.visitors_total === null && (
              <Card className="p-4 mb-6 border-emerald-200 bg-emerald-50/60 text-sm text-emerald-900" data-testid="admin-visitors-note">
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="mt-0.5" />
                  <div>
                    <strong>Google Analytics 4 connected</strong> (tag <span className="font-mono">G-JWMQ511E72</span>, production-only).
                    Visitor counts, page views and conversion events live in <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" className="underline font-semibold">analytics.google.com</a> (data appears with a 24–48h delay for non-Realtime reports).
                    To wire <strong>Google Search Console</strong>, verify <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="underline font-semibold">https://www.carsaledoc.com</a> there using the GA tag — verification is one click since GA is already live.
                  </div>
                </div>
              </Card>
            )}

            <Tabs defaultValue="funnel" className="space-y-6">
              <TabsList data-testid="admin-tabs">
                <TabsTrigger value="funnel" data-testid="tab-funnel">Funnel</TabsTrigger>
                <TabsTrigger value="states" data-testid="tab-states">By state</TabsTrigger>
                <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
                <TabsTrigger value="calc-leads" data-testid="tab-calc-leads">Calc leads</TabsTrigger>
                <TabsTrigger value="messages" data-testid="tab-messages">
                  Messages
                  {unreadMessages > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[var(--csd-danger)] text-white text-[10px] font-bold px-1.5">
                      {unreadMessages}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                <TabsTrigger value="errors" data-testid="tab-errors">Errors</TabsTrigger>
              </TabsList>

              {/* FUNNEL */}
              <TabsContent value="funnel">
                <Card className="p-6">
                  <h2 className="font-heading text-lg font-bold mb-4">Conversion funnel</h2>
                  <ul className="space-y-2" data-testid="funnel-list">
                    {funnel.map((step, idx) => {
                      const maxCount = funnel.reduce((m, s) => Math.max(m, s.count || 0), 0) || 1;
                      const width = step.count == null ? 0 : (step.count / maxCount) * 100;
                      return (
                        <li key={step.key} className="grid grid-cols-[36px_1fr_auto] gap-3 items-center">
                          <div className="w-9 h-9 rounded-full bg-[var(--csd-primary)] text-white grid place-items-center text-sm font-bold tabular-nums">
                            {idx + 1}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{step.label}</div>
                            <div className="h-2.5 rounded-full bg-[var(--csd-primary-light)]/40 mt-2 overflow-hidden">
                              <div
                                className="h-full bg-[var(--csd-primary)] rounded-full transition-all"
                                style={{ width: `${width}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-right tabular-nums min-w-[110px]">
                            <div className="font-bold text-gray-900">{step.count == null ? "—" : fmtN(step.count)}</div>
                            <div className="text-xs text-[var(--csd-text-secondary)]">
                              {step.pct_from_prev != null ? `${step.pct_from_prev}% from prev` : "—"}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Card>

                {/* WIZARD STEP DROP-OFF */}
                <Card className="p-6 mt-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <h2 className="font-heading text-lg font-bold">Wizard drop-off</h2>
                    <span className="text-xs text-[var(--csd-text-secondary)]">— where users abandon the 12-step builder</span>
                  </div>
                  <p className="text-xs text-[var(--csd-text-secondary)] mb-4">
                    Each row shows how many agreements reached that step, how many stopped there, and the % loss to the next step.
                  </p>
                  <div className="overflow-x-auto" data-testid="wizard-funnel">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--csd-primary-light)]/30 text-[var(--csd-text-secondary)] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Step</th>
                          <th className="text-right px-3 py-2">Reached</th>
                          <th className="text-right px-3 py-2">Stopped here</th>
                          <th className="text-right px-3 py-2">Drop to next</th>
                          <th className="text-left px-3 py-2 w-[35%]">Funnel</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--csd-border)]">
                        {wizardFunnel.map((row) => {
                          const maxReached = wizardFunnel.reduce((m, r) => Math.max(m, r.reached || 0), 0) || 1;
                          const w = ((row.reached || 0) / maxReached) * 100;
                          const dropPct = row.pct_drop_to_next ?? 0;
                          const dropColor = dropPct >= 50 ? "text-[var(--csd-danger)]" : dropPct >= 25 ? "text-amber-600" : "text-[var(--csd-text-secondary)]";
                          return (
                            <tr key={row.step}>
                              <td className="px-3 py-2 font-medium text-gray-900">{row.label}</td>
                              <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtN(row.reached || 0)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtN(row.dropped_here || 0)}</td>
                              <td className={`px-3 py-2 text-right tabular-nums font-semibold ${dropColor}`}>
                                {row.pct_drop_to_next == null ? "—" : `${row.pct_drop_to_next}%`}
                              </td>
                              <td className="px-3 py-2">
                                <div className="h-2 rounded-full bg-[var(--csd-primary-light)]/40 overflow-hidden">
                                  <div className="h-full bg-[var(--csd-primary)] rounded-full" style={{ width: `${w}%` }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {wizardFunnel.length === 0 && (
                          <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--csd-text-secondary)]">No wizard activity in this range.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* BY STATE */}
              <TabsContent value="states">
                <Card className="p-0 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="by-state-table">
                      <thead className="bg-[var(--csd-primary-light)]/30 text-[var(--csd-text-secondary)] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-3">State</th>
                          <th className="text-right px-4 py-3">Forms started</th>
                          <th className="text-right px-4 py-3">Documents</th>
                          <th className="text-right px-4 py-3">Payments</th>
                          <th className="text-right px-4 py-3">Revenue</th>
                          <th className="text-right px-4 py-3">Conv.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--csd-border)]">
                        {byState.map((r) => (
                          <tr key={r.state} className="hover:bg-[var(--csd-bg)]/40">
                            <td className="px-4 py-3 font-semibold text-gray-900">
                              <MapPin size={12} className="inline-block mr-1 text-[var(--csd-primary)]" />
                              {r.state}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">{fmtN(r.forms_started)}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{fmtN(r.documents_generated)}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{fmtN(r.payments)}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtUSD(r.revenue_usd)}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{fmtPct(r.conversion_rate_pct)}</td>
                          </tr>
                        ))}
                        {byState.length === 0 && (
                          <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--csd-text-secondary)]">No data in range.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* PAYMENTS */}
              <TabsContent value="payments">
                <Card className="p-0 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="payments-table">
                      <thead className="bg-[var(--csd-primary-light)]/30 text-[var(--csd-text-secondary)] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-3">Date</th>
                          <th className="text-left px-4 py-3">Customer</th>
                          <th className="text-left px-4 py-3">State</th>
                          <th className="text-left px-4 py-3">Product</th>
                          <th className="text-right px-4 py-3">Amount</th>
                          <th className="text-left px-4 py-3">Payment</th>
                          <th className="text-left px-4 py-3">Session</th>
                          <th className="text-left px-4 py-3">Stripe ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--csd-border)]">
                        {payments.map((p) => (
                          <tr key={p.stripe_session_id || p.agreement_id} className="hover:bg-[var(--csd-bg)]/40">
                            <td className="px-4 py-3 whitespace-nowrap text-[var(--csd-text-secondary)]">{fmtDate(p.created_at)}</td>
                            <td className="px-4 py-3 font-mono text-xs break-all max-w-[200px]">{p.customer_email || "—"}</td>
                            <td className="px-4 py-3">{p.state}</td>
                            <td className="px-4 py-3 text-[var(--csd-text-secondary)]">{p.product}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtUSD(p.amount_usd)}</td>
                            <td className="px-4 py-3">
                              <Badge
                                variant={p.payment_status === "paid" ? "default" : "outline"}
                                className={p.payment_status === "paid" ? "bg-[var(--csd-primary)]" : ""}
                              >
                                {p.payment_status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-xs text-[var(--csd-text-secondary)]">{p.session_status}</td>
                            <td className="px-4 py-3 font-mono text-xs break-all max-w-[180px]">{p.stripe_session_id || "—"}</td>
                          </tr>
                        ))}
                        {payments.length === 0 && (
                          <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--csd-text-secondary)]">No payments in range.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* LEADS */}
              <TabsContent value="leads">
                <Card className="p-0 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="leads-table">
                      <thead className="bg-[var(--csd-primary-light)]/30 text-[var(--csd-text-secondary)] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-3">Created</th>
                          <th className="text-left px-4 py-3">Email</th>
                          <th className="text-left px-4 py-3">Name</th>
                          <th className="text-left px-4 py-3">State</th>
                          <th className="text-left px-4 py-3">Doc type</th>
                          <th className="text-left px-4 py-3">Step</th>
                          <th className="text-left px-4 py-3">Paid?</th>
                          <th className="text-right px-4 py-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--csd-border)]">
                        {leads.map((l) => (
                          <tr key={l.id} className="hover:bg-[var(--csd-bg)]/40">
                            <td className="px-4 py-3 whitespace-nowrap text-[var(--csd-text-secondary)]">{fmtDate(l.created_at)}</td>
                            <td className="px-4 py-3 font-mono text-xs break-all max-w-[200px]">{l.email || "—"}</td>
                            <td className="px-4 py-3">{l.full_name || "—"}</td>
                            <td className="px-4 py-3">{l.state}</td>
                            <td className="px-4 py-3 text-[var(--csd-text-secondary)]">{l.document_type}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="font-mono text-xs">{l.step_reached}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              {l.paid ? (
                                <Badge className="bg-[var(--csd-primary)]">Paid</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[var(--csd-text-secondary)]">Not paid</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">{l.paid ? fmtUSD(l.amount_usd) : "—"}</td>
                          </tr>
                        ))}
                        {leads.length === 0 && (
                          <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--csd-text-secondary)]">No leads in range.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* CALCULATOR LEADS */}
              <TabsContent value="calc-leads">
                <Card className="p-0 overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--csd-border)] flex items-center gap-3 bg-[var(--csd-primary-light)]/15">
                    <Calculator size={16} className="text-[var(--csd-primary)]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-sm text-gray-900">Private Sale Value Calculator leads</div>
                      <div className="text-xs text-[var(--csd-text-secondary)]">
                        Every estimate run on the calculator generates a row. Email is captured only when the visitor asks for the full PDF report.
                      </div>
                    </div>
                    <Badge variant="outline" className="font-mono shrink-0" data-testid="calc-leads-count">
                      {calcLeads.length} {calcLeads.length === 1 ? "row" : "rows"}
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="calc-leads-table">
                      <thead className="bg-[var(--csd-primary-light)]/30 text-[var(--csd-text-secondary)] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-3">Created</th>
                          <th className="text-left px-4 py-3">Email</th>
                          <th className="text-left px-4 py-3">Vehicle</th>
                          <th className="text-left px-4 py-3">Mileage</th>
                          <th className="text-left px-4 py-3">Condition</th>
                          <th className="text-left px-4 py-3">State / ZIP</th>
                          <th className="text-right px-4 py-3">Estimate</th>
                          <th className="text-center px-4 py-3">→ Builder</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--csd-border)]">
                        {calcLeads.map((l) => (
                          <tr key={l.id} className="hover:bg-[var(--csd-bg)]/40">
                            <td className="px-4 py-3 whitespace-nowrap text-[var(--csd-text-secondary)]">{fmtDate(l.created_at)}</td>
                            <td className="px-4 py-3 font-mono text-xs break-all max-w-[220px]">
                              {l.email ? (
                                <span className="text-gray-900">{l.email}</span>
                              ) : (
                                <span className="text-[var(--csd-text-secondary)]">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-semibold">{l.vehicle_label}</td>
                            <td className="px-4 py-3 tabular-nums text-[var(--csd-text-secondary)]">{l.mileage ? `${l.mileage.toLocaleString()} mi` : "—"}</td>
                            <td className="px-4 py-3 text-[var(--csd-text-secondary)] capitalize">{l.condition || "—"}</td>
                            <td className="px-4 py-3">
                              <span className="font-semibold">{l.state}</span>
                              <span className="text-[var(--csd-text-secondary)] ml-1">{l.zip !== "—" ? `· ${l.zip}` : ""}</span>
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {l.coverage === "exact" && l.estimate_low != null ? (
                                <span className="font-semibold text-gray-900">{fmtUSD(l.estimate_low)} – {fmtUSD(l.estimate_high)}</span>
                              ) : (
                                <Badge variant="outline" className="text-[var(--csd-text-secondary)]">No coverage</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {l.clicked_to_builder ? (
                                <Badge className="bg-[var(--csd-primary)]" data-testid="calc-lead-converted">Yes</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[var(--csd-text-secondary)]">—</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                        {calcLeads.length === 0 && (
                          <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--csd-text-secondary)]">No calculator leads in range.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* MESSAGES */}
              <TabsContent value="messages">
                <ContactInbox messages={messages} onChange={loadAll} />
              </TabsContent>

              {/* DOCUMENTS */}
              <TabsContent value="documents">
                <Card className="p-0 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="documents-table">
                      <thead className="bg-[var(--csd-primary-light)]/30 text-[var(--csd-text-secondary)] text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-3">Document type</th>
                          <th className="text-left px-4 py-3">State</th>
                          <th className="text-left px-4 py-3">Paid / Free</th>
                          <th className="text-right px-4 py-3">Count</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--csd-border)]">
                        {documents.map((d, i) => (
                          <tr key={i} className="hover:bg-[var(--csd-bg)]/40">
                            <td className="px-4 py-3 font-semibold">{d.document_type}</td>
                            <td className="px-4 py-3">{d.state}</td>
                            <td className="px-4 py-3">
                              {d.paid_or_free === "paid" ? (
                                <Badge className="bg-[var(--csd-primary)]">Paid</Badge>
                              ) : (
                                <Badge variant="outline">Free preview</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtN(d.count)}</td>
                          </tr>
                        ))}
                        {documents.length === 0 && (
                          <tr><td colSpan={4} className="px-4 py-10 text-center text-[var(--csd-text-secondary)]">No documents in range.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* ERRORS */}
              <TabsContent value="errors">
                <Card className="p-6">
                  <h2 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-500" /> Backend errors
                  </h2>
                  <p className="text-xs text-[var(--csd-text-secondary)] mb-4">
                    Last {errs?.lines?.length || 0} error / exception lines from backend logs (API keys redacted).
                  </p>
                  <div className="font-mono text-xs bg-gray-900 text-amber-100 rounded-xl p-4 max-h-[480px] overflow-auto" data-testid="errors-pane">
                    {(errs?.lines || []).length === 0 ? (
                      <div className="text-emerald-300">No errors logged. Everything looks healthy.</div>
                    ) : (
                      (errs?.lines || []).map((ln, i) => (
                        <div key={i} className="whitespace-pre-wrap break-all leading-relaxed">{ln}</div>
                      ))
                    )}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>

            <footer className="text-center text-xs text-[var(--csd-text-secondary)] mt-10 pt-6 border-t border-[var(--csd-border)]">
              CarSaleDoc admin · data refreshed in real time from production MongoDB · noindex
            </footer>
          </>
        )}
      </main>
    </div>
  );
}

// -------------------- Top-level page --------------------
export default function Admin() {
  const [auth, setAuth] = useState({ status: "checking", user: null });

  const checkAuth = async () => {
    try {
      const me = await adminApi.me();
      setAuth({ status: "authed", user: me });
    } catch {
      setAuth({ status: "unauth", user: null });
    }
  };

  useAuthCallback((user) => {
    if (user) setAuth({ status: "authed", user });
    else setAuth({ status: "unauth", user: null });
  });

  useEffect(() => {
    // If we're coming back from OAuth, useAuthCallback handles the exchange
    // and updates state. Otherwise, ask the server who we are.
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, []);

  if (auth.status === "checking") {
    return (
      <div className="min-h-screen bg-[var(--csd-bg)] grid place-items-center">
        <SEO title="Admin — CarSaleDoc" description="Internal admin panel." path="/admin" noindex />
        <div className="text-center text-[var(--csd-text-secondary)]">
          <Loader2 size={28} className="animate-spin text-[var(--csd-primary)] mx-auto" />
          <div className="mt-3 text-sm">Checking session…</div>
        </div>
      </div>
    );
  }

  if (auth.status === "unauth") {
    return <AdminLogin />;
  }

  return (
    <AdminDashboard
      user={auth.user}
      onLogout={() => setAuth({ status: "unauth", user: null })}
    />
  );
}
